import Stack from '@mui/material/Stack';
import { alpha, useTheme } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import { useState, useEffect } from 'react';
import Box from '@mui/material/Box';

import { SplashScreen } from 'src/components/loading-screen';
import Logo from 'src/components/logo';
import { useResponsive } from 'src/hooks/use-responsive';
import { useWhitelabelStore } from 'src/store/whitelabel/whitelabel.store';
import { bgGradient } from 'src/theme/css';

// ----------------------------------------------------------------------

type Props = {
  title?: string;
  image?: string;
  children: React.ReactNode;
};

export default function AuthClassicLayout({ children, image, title }: Props) {
  const theme = useTheme();
  const mdUp = useResponsive('up', 'md');

  const [loading, setLoading] = useState(true);
  const [loginImage, setLoginImage] = useState(image || '/logo/logo-nectar-horizontal-laranja.png');

  useEffect(() => {
    const { whitelabel } = useWhitelabelStore.getState();
    if (whitelabel?.loginImage) {
      setLoginImage(whitelabel.loginImage);
    }
    // IMPORTANTE: Sempre chamar setLoading(false), com ou sem whitelabel
    setLoading(false);
  }, []);

  const renderLogo = (
    <Logo
      customLogo={useWhitelabelStore.getState().whitelabel?.logo ?? '/logo/logo-nectar-laranja.png'}
      sx={{
        zIndex: 9,
        position: 'absolute',
        mt: { xs: 2, md: 3 },
        mb: { xs: 2, md: 5 },
        ml: { xs: 2, md: 4 },
        mr: { xs: 2, md: 5 },
      }}
    />
  );

  const renderContent = (
    <Stack
      sx={{
        width: 1,
        mx: 'auto',
        maxWidth: 480,
        px: { xs: 2, md: 8 },
        pt: { xs: 15, md: 20 },
        pb: { xs: 15, md: 0 },
      }}
    >
      {children}
    </Stack>
  );

  const renderSection = (
    <Stack
      flexGrow={1}
      spacing={10}
      alignItems="center"
      justifyContent="center"
      sx={{
        ...bgGradient({
          color: alpha(theme.palette.background.default, theme.palette.mode === 'light' ? 0.88 : 0.94),
          imgUrl: '/assets/background/overlay_2.jpg',
        }),
      }}
    >
      <Typography variant="h3" sx={{ maxWidth: 480, textAlign: 'center' }}>
        {title || ''}
      </Typography>

      <Box
        component="img"
        alt="auth"
        src={loginImage}
        sx={{
          maxWidth: {
            xs: 480,
            lg: 560,
            xl: 720,
          },
        }}
      />
    </Stack>
  );

  return loading ? (
    <SplashScreen />
  ) : (
    <Stack
      component="main"
      direction="row"
      sx={{
        minHeight: '100vh',
      }}
    >
      {renderLogo}

      {mdUp && renderSection}

      {renderContent}
    </Stack>
  );
}
